// Faturamento por Hotel (ticket #19). A Fatura de um Cliente num Período cobra
// as Devoluções cuja data cai no Período: Contagem Devolvida + Devolvidas de
// Retidas, cada Peça pelo preço vigente **na data da Devolução** (uma peça
// retida é cobrada quando volta, nunca no Lote de origem — ADR-0007). A
// exceção é a Compensação de Peças (ADR-0031): corrige uma OS já faturada e
// é cobrada pelo preço da data daquela OS.
//
// Rascunho recalcula sozinho; emitir congela os valores, dá o número anual e
// trava os Lotes. Reabrir volta ao rascunho, guardando a justificativa.

import { newId, type Id } from "./ids.ts";
import { formatData, isDataISO, type DataISO } from "./datas.ts";
import type { Centavos } from "./money.ts";
import { precoVigente } from "./precificacao.ts";
import { baixasDoLote, tiposBaixadosDoLote } from "./backlog.ts";
import { loteEstaFaturado } from "./lotes.ts";
import { vencimentoSugerido } from "./vencimento.ts";
import {
  contaDaFatura,
  criarConta,
  registrarPagamento,
  removerContaDaFatura,
  situacaoPagamento,
  totalPago,
} from "./contas.ts";
import {
  categoriaFaturamento,
  garantirCategoriaFaturamento,
} from "./categorias-financeiras.ts";
import type {
  AjusteFatura,
  Cliente,
  CompensacaoFatura,
  Lote,
  PecaCompensada,
  DataFaturamento,
  Fatura,
  LinhaFaturaCongelada,
  Periodo,
  Snapshot,
} from "./snapshot.ts";

export type { Fatura, Periodo, AjusteFatura };

export class ErroFatura extends Error {}

// ---------------------------------------------------------------- período ----

function ultimoDiaDoMes(ano: number, mes1a12: number): number {
  return new Date(ano, mes1a12, 0).getDate();
}

function iso(ano: number, mes1a12: number, dia: number): DataISO {
  return `${ano}-${String(mes1a12).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

/**
 * O último Período **já fechado** conforme o Ciclo de Fechamento do Cliente.
 * É só uma sugestão — a tela deixa ajustar.
 */
export function periodoSugerido(
  cliente: Pick<Cliente, "cicloFechamento">,
  hoje: DataISO = new Date().toISOString().slice(0, 10),
): Periodo {
  const [ano, mes, dia] = hoje.split("-").map(Number) as [number, number, number];
  const ciclo = cliente.cicloFechamento;

  if (ciclo.tipo === "mensal") {
    const m = mes === 1 ? 12 : mes - 1;
    const y = mes === 1 ? ano - 1 : ano;
    return { de: iso(y, m, 1), ate: iso(y, m, ultimoDiaDoMes(y, m)) };
  }

  if (ciclo.tipo === "quinzenal") {
    // 1–15 e 16–fim; devolve a última quinzena inteira que já passou
    if (dia > 15) return { de: iso(ano, mes, 1), ate: iso(ano, mes, 15) };
    const m = mes === 1 ? 12 : mes - 1;
    const y = mes === 1 ? ano - 1 : ano;
    return { de: iso(y, m, 16), ate: iso(y, m, ultimoDiaDoMes(y, m)) };
  }

  // customizado: janela [diaInicio .. diaFim], podendo cruzar o mês
  const { diaInicio, diaFim } = ciclo;
  if (diaInicio <= diaFim) {
    // janela dentro do mês: a última que terminou antes de hoje
    let y = ano;
    let m = mes;
    if (dia <= diaFim) {
      m = mes === 1 ? 12 : mes - 1;
      y = mes === 1 ? ano - 1 : ano;
    }
    const fim = Math.min(diaFim, ultimoDiaDoMes(y, m));
    return { de: iso(y, m, diaInicio), ate: iso(y, m, fim) };
  }
  // janela que cruza o mês (ex.: 21 → 20): [mês-1, diaInicio] .. [mês, diaFim]
  let yFim = ano;
  let mFim = mes;
  if (dia <= diaFim) {
    mFim = mes === 1 ? 12 : mes - 1;
    yFim = mes === 1 ? ano - 1 : ano;
  }
  const mIni = mFim === 1 ? 12 : mFim - 1;
  const yIni = mFim === 1 ? yFim - 1 : yFim;
  return {
    de: iso(yIni, mIni, Math.min(diaInicio, ultimoDiaDoMes(yIni, mIni))),
    ate: iso(yFim, mFim, Math.min(diaFim, ultimoDiaDoMes(yFim, mFim))),
  };
}

/**
 * A 1ª (dia 1 ao 15) ou a 2ª (dia 16 ao último dia) quinzena do **mês corrente**
 * de `hoje`. Usada na tela de Nova Fatura quando o hotel fecha quinzenalmente.
 */
export function quinzenaDoMes(
  qual: 1 | 2,
  hoje: DataISO = new Date().toISOString().slice(0, 10),
): Periodo {
  const [ano, mes] = hoje.split("-").map(Number) as [number, number];
  return qual === 1
    ? { de: iso(ano, mes, 1), ate: iso(ano, mes, 15) }
    : { de: iso(ano, mes, 16), ate: iso(ano, mes, ultimoDiaDoMes(ano, mes)) };
}

function validarPeriodo(p: Periodo): Periodo {
  if (!isDataISO(p.de) || !isDataISO(p.ate)) {
    throw new ErroFatura("Informe as datas do período.");
  }
  if (p.de > p.ate) {
    throw new ErroFatura("O início do período é depois do fim.");
  }
  return p;
}

const sobrepoe = (a: Periodo, b: Periodo): boolean =>
  a.de <= b.ate && b.de <= a.ate;

// ------------------------------------------------------------- rascunho ----

export function criarFaturaRascunho(
  snapshot: Snapshot,
  clienteId: Id,
  periodo: Periodo,
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === clienteId)) {
    throw new ErroFatura("Cliente não encontrado.");
  }
  const p = validarPeriodo(periodo);
  const conflito = snapshot.faturas.find(
    (f) => f.clienteId === clienteId && sobrepoe(f.periodo, p),
  );
  if (conflito) {
    const qual = conflito.numero ? `a fatura ${conflito.numero}` : "um rascunho";
    throw new ErroFatura(
      `O período se sobrepõe a ${qual} deste cliente. Reabra ou ajuste essa fatura.`,
    );
  }
  const fatura: Fatura = {
    id: newId(),
    clienteId,
    periodo: p,
    numero: null,
    emitidaEm: null,
    ajustes: [],
    compensacoes: [],
    loteIds: [],
    linhasCongeladas: [],
    reaberturas: [],
    vencimento: null,
  };
  return { ...snapshot, faturas: [...snapshot.faturas, fatura] };
}

export interface ResultadoFaturaEmMassa {
  abertas: { clienteId: Id; faturaId: Id }[];
  puladas: { clienteId: Id; motivo: string }[];
}

/**
 * Abre um rascunho de Fatura no mesmo Período para cada Cliente da lista, numa
 * única transição de Snapshot. Cliente cuja Fatura desse Período já existe (ou
 * cujo período é inválido) é pulado, com o motivo — os demais seguem abrindo.
 */
export function abrirFaturasEmMassa(
  snapshot: Snapshot,
  clienteIds: readonly Id[],
  periodo: Periodo,
): { snapshot: Snapshot; resultado: ResultadoFaturaEmMassa } {
  let out = snapshot;
  const abertas: ResultadoFaturaEmMassa["abertas"] = [];
  const puladas: ResultadoFaturaEmMassa["puladas"] = [];
  for (const clienteId of clienteIds) {
    try {
      const antes = out.faturas.length;
      out = criarFaturaRascunho(out, clienteId, periodo);
      abertas.push({ clienteId, faturaId: out.faturas[antes]!.id });
    } catch (e) {
      puladas.push({
        clienteId,
        motivo: e instanceof ErroFatura ? e.message : "Não foi possível abrir.",
      });
    }
  }
  return { snapshot: out, resultado: { abertas, puladas } };
}

function pegar(snapshot: Snapshot, faturaId: Id): Fatura {
  const f = snapshot.faturas.find((x) => x.id === faturaId);
  if (!f) throw new ErroFatura("Fatura não encontrada.");
  return f;
}

function exigirRascunho(f: Fatura): void {
  if (f.emitidaEm != null) {
    throw new ErroFatura("A Fatura está emitida. Reabra antes de editar.");
  }
}

function trocar(snapshot: Snapshot, fatura: Fatura): Snapshot {
  return {
    ...snapshot,
    faturas: snapshot.faturas.map((f) => (f.id === fatura.id ? fatura : f)),
  };
}

export function editarPeriodoFatura(
  snapshot: Snapshot,
  faturaId: Id,
  periodo: Periodo,
): Snapshot {
  const f = pegar(snapshot, faturaId);
  exigirRascunho(f);
  const p = validarPeriodo(periodo);
  const conflito = snapshot.faturas.find(
    (x) => x.id !== faturaId && x.clienteId === f.clienteId && sobrepoe(x.periodo, p),
  );
  if (conflito) {
    throw new ErroFatura("O novo período se sobrepõe a outra fatura deste cliente.");
  }
  return trocar(snapshot, { ...f, periodo: p });
}

export function excluirFatura(snapshot: Snapshot, faturaId: Id): Snapshot {
  const f = pegar(snapshot, faturaId);
  if (f.emitidaEm != null) {
    throw new ErroFatura("Não dá para excluir uma fatura emitida.");
  }
  return { ...snapshot, faturas: snapshot.faturas.filter((x) => x.id !== faturaId) };
}

// -------------------------------------------------------------- ajustes ----

export function adicionarAjuste(
  snapshot: Snapshot,
  faturaId: Id,
  dados: { descricao: string; valorCentavos: Centavos; tipoId?: Id | null },
): Snapshot {
  const f = pegar(snapshot, faturaId);
  exigirRascunho(f);
  const descricao = dados.descricao.trim();
  if (descricao === "") throw new ErroFatura("O ajuste precisa de uma descrição.");
  if (!Number.isFinite(dados.valorCentavos) || dados.valorCentavos === 0) {
    throw new ErroFatura("O ajuste precisa de um valor diferente de zero.");
  }
  const tipoId = dados.tipoId ?? null;
  if (tipoId != null && !snapshot.tiposPeca.some((t) => t.id === tipoId)) {
    throw new ErroFatura("Tipo de peça do ajuste não encontrado.");
  }
  const ajuste: AjusteFatura = {
    id: newId(),
    descricao,
    valorCentavos: Math.round(dados.valorCentavos),
    tipoId,
  };
  return trocar(snapshot, { ...f, ajustes: [...f.ajustes, ajuste] });
}

export function removerAjuste(
  snapshot: Snapshot,
  faturaId: Id,
  ajusteId: Id,
): Snapshot {
  const f = pegar(snapshot, faturaId);
  exigirRascunho(f);
  return trocar(snapshot, {
    ...f,
    ajustes: f.ajustes.filter((a) => a.id !== ajusteId),
  });
}

// -------------------------------------------------------- compensações ----

export interface DadosCompensacao {
  /** A OS em que a falha aconteceu. */
  os: number;
  /** Data da OS — só usada quando ela não está no sistema. */
  data: DataISO | null;
  motivo: string;
  pecas: PecaCompensada[];
}

/**
 * Onde está a OS de uma compensação, vista da Fatura (ADR-0031):
 * - `faturada`: no sistema e já numa fatura emitida → a data é a dela;
 * - `foraDoSistema`: anterior ao sistema → a data precisa ser informada;
 * - `nestaFatura` / `naoFaturada`: não se compensa — corrige-se pelo
 *   Fechamento de OS;
 * - `cancelada`: não se compensa — não foi nem será cobrada.
 */
export type OsCompensacao =
  | { tipo: "faturada"; lote: Lote; data: DataISO }
  | { tipo: "foraDoSistema" }
  | { tipo: "nestaFatura" }
  | { tipo: "naoFaturada" }
  | { tipo: "cancelada" };

/** Só estas duas situações aceitam compensação; as outras têm outro caminho. */
export function osCompensavel(
  onde: OsCompensacao,
): onde is Extract<OsCompensacao, { tipo: "faturada" | "foraDoSistema" }> {
  return onde.tipo === "faturada" || onde.tipo === "foraDoSistema";
}

export function localizarOsCompensacao(snapshot: Snapshot, faturaId: Id, os: number): OsCompensacao {
  const f = pegar(snapshot, faturaId);
  const doCliente = snapshot.lotes.filter((l) => l.clienteId === f.clienteId && l.os === os);
  // a OS ativa vale; uma cancelada só conta se for a única com o número
  const lote = doCliente.find((l) => !l.canceladoEm);
  if (!lote) {
    const cancelada = doCliente[0];
    return cancelada ? { tipo: "cancelada" } : { tipo: "foraDoSistema" };
  }
  if (calcularFatura(snapshot, faturaId).movimentos.some((m) => m.loteId === lote.id)) {
    return { tipo: "nestaFatura" };
  }
  if (!loteEstaFaturado(snapshot, lote.id)) return { tipo: "naoFaturada" };
  return { tipo: "faturada", lote, data: dataRefLote(lote, snapshot.config.dataFaturamento) };
}

/** A frase que explica a situação da OS — a mesma na tela e no erro. */
export function mensagemOsCompensacao(os: number, onde: OsCompensacao): string {
  switch (onde.tipo) {
    case "faturada":
      return `OS ${os} encontrada: ${formatData(onde.data)} (${onde.lote.codigo}).`;
    case "foraDoSistema":
      return `A OS ${os} não está no sistema — informe a data dela.`;
    case "nestaFatura":
      return `A OS ${os} está nesta fatura — corrija pelo Fechamento de OS.`;
    case "naoFaturada":
      return `A OS ${os} ainda não foi faturada — corrija pelo Fechamento de OS.`;
    case "cancelada":
      return `A OS ${os} foi cancelada — não há o que compensar.`;
  }
}

/**
 * Compensa peças de uma OS numa Fatura em rascunho (ADR-0031). A OS precisa
 * estar fora desta fatura: se está nela, ou no sistema e ainda não faturada, a
 * correção é pelo Fechamento de OS. OS no sistema dá a data (a de
 * referência do faturamento); OS anterior ao sistema pede a data. Nenhuma
 * linha da fatura pode ficar negativa.
 */
export function adicionarCompensacao(snapshot: Snapshot, faturaId: Id, dados: DadosCompensacao): Snapshot {
  const f = pegar(snapshot, faturaId);
  exigirRascunho(f);
  if (!Number.isInteger(dados.os) || dados.os <= 0) throw new ErroFatura("Informe a OS.");
  const motivo = dados.motivo.trim();
  if (motivo === "") throw new ErroFatura("Informe o motivo da compensação.");
  if (dados.pecas.length === 0) throw new ErroFatura("Adicione ao menos uma peça.");
  const vistos = new Set<Id>();
  for (const p of dados.pecas) {
    if (!snapshot.tiposPeca.some((t) => t.id === p.tipoId)) throw new ErroFatura("Tipo de peça não encontrado.");
    if (vistos.has(p.tipoId)) throw new ErroFatura("A mesma peça apareceu duas vezes na compensação.");
    vistos.add(p.tipoId);
    if (!Number.isInteger(p.quantidade) || p.quantidade === 0) {
      throw new ErroFatura("Quantidade inválida: use um número inteiro diferente de zero.");
    }
  }

  const onde = localizarOsCompensacao(snapshot, faturaId, dados.os);
  let data: DataISO;
  if (!osCompensavel(onde)) {
    throw new ErroFatura(mensagemOsCompensacao(dados.os, onde));
  } else if (onde.tipo === "faturada") {
    data = onde.data;
  } else {
    if (dados.data == null || !isDataISO(dados.data)) {
      throw new ErroFatura(mensagemOsCompensacao(dados.os, onde));
    }
    data = dados.data;
  }

  const compensacao: CompensacaoFatura = {
    id: newId(),
    os: dados.os,
    data,
    loteId: onde.tipo === "faturada" ? onde.lote.id : null,
    motivo,
    pecas: dados.pecas.map((p) => ({ tipoId: p.tipoId, quantidade: p.quantidade })),
  };
  const depois = trocar(snapshot, { ...f, compensacoes: [...f.compensacoes, compensacao] });
  exigirLinhasNaoNegativas(depois, faturaId);
  return depois;
}

export function removerCompensacao(snapshot: Snapshot, faturaId: Id, compensacaoId: Id): Snapshot {
  const f = pegar(snapshot, faturaId);
  exigirRascunho(f);
  if (!f.compensacoes.some((c) => c.id === compensacaoId)) throw new ErroFatura("Compensação não encontrada.");
  const depois = trocar(snapshot, { ...f, compensacoes: f.compensacoes.filter((c) => c.id !== compensacaoId) });
  exigirLinhasNaoNegativas(depois, faturaId);
  return depois;
}

/**
 * A OS compensada que está no sistema precisa continuar faturada até a
 * emissão: se a fatura dela foi reaberta, a OS voltou a ser corrigível pelo
 * Fechamento — compensar também corrigiria duas vezes.
 */
function exigirCompensacoesDeOsFaturada(snapshot: Snapshot, f: Fatura): void {
  for (const c of f.compensacoes) {
    if (c.loteId != null && !loteEstaFaturado(snapshot, c.loteId)) {
      throw new ErroFatura(
        `A OS ${c.os} compensada teve a fatura reaberta — corrija pelo Fechamento de OS e remova a compensação.`,
      );
    }
  }
}

function nomeDoTipo(snapshot: Snapshot, tipoId: Id): string {
  return snapshot.tiposPeca.find((t) => t.id === tipoId)?.nome ?? "peça";
}

/** Uma compensação negativa não pode tirar mais peças do que a fatura tem. */
function exigirLinhasNaoNegativas(snapshot: Snapshot, faturaId: Id): void {
  for (const l of calcularFatura(snapshot, faturaId).linhas) {
    if (l.quantidade < 0) {
      const nome = nomeDoTipo(snapshot, l.tipoId);
      throw new ErroFatura(`${nome} ficaria com ${l.quantidade} na fatura — a compensação tira mais peças do que ela tem.`);
    }
  }
}

// ----------------------------------------------------------- cálculo ----

/** Um item devolvido dentro do Período, antes de agregar por Tipo. */
export interface MovimentoFatura {
  loteId: Id;
  loteCodigo: string;
  os: number;
  /** Data de referência do Lote — coleta ou devolução, conforme `config`. */
  data: DataISO;
  tipoId: Id;
  /** Devolvida deste Lote. */
  devolvida: number;
  /** Devolvida de retidas (Backlog) nesta mesma Devolução. */
  deRetidas: number;
  /** `null` = preço pendente nessa data. */
  precoCentavos: Centavos | null;
}

export interface LinhaFatura {
  tipoId: Id;
  quantidade: number;
  deRetidas: number;
  subtotalCentavos: Centavos;
  /** Preço médio ponderado (subtotal ÷ quantidade), em centavos. */
  unitarioCentavos: Centavos;
  /**
   * Os preços unitários usados, em ordem de data (o primeiro uso de cada um).
   * Mais de um quando o preço mudou no período ou há Compensação de Peças —
   * aí a média não é um preço da tabela e não fecha na conferência. Vazio só
   * em fatura emitida antes deste campo existir.
   */
  precosCentavos: Centavos[];
  /** Ajustes presos a este Tipo. */
  ajustes: AjusteFatura[];
  /** Algum preço deste Tipo no Período está pendente. */
  pendente: boolean;
}

export interface FaturaCalculada {
  fatura: Fatura;
  emitida: boolean;
  linhas: LinhaFatura[];
  /** Ajustes de linha livre (sem Tipo). */
  ajustesLivres: AjusteFatura[];
  movimentos: MovimentoFatura[];
  subtotalPecasCentavos: Centavos;
  ajustesCentavos: Centavos;
  totalCentavos: Centavos;
  /** Tipos com preço pendente no Período — bloqueiam a emissão. */
  tiposPendentes: Id[];
}

/**
 * A data de referência de um Lote no faturamento, conforme
 * `config.dataFaturamento`: a da coleta (padrão) ou a da devolução. Uma Peça que
 * volta de retidas usa a data do Lote **carona** (aquele em que veio) — ADR-0008.
 */
function dataRefLote(lote: Snapshot["lotes"][number], modo: DataFaturamento): DataISO {
  return modo === "devolucao" ? (lote.devolvidoEm ?? lote.coletaData) : lote.coletaData;
}

/**
 * As OS devolvidas do Cliente no Período da Fatura, pela data de referência.
 * `relavagem` escolhe quais: as cobradas (`false` — ADR-0015: OS de relavagem
 * não é cobrada) ou as de relavagem, que a Planilha só mostra (`true`).
 */
function devolucoesDoPeriodo(snapshot: Snapshot, fatura: Fatura, relavagem = false): typeof snapshot.lotes {
  const modo = snapshot.config.dataFaturamento;
  return snapshot.lotes
    .filter(
      (l) =>
        l.clienteId === fatura.clienteId &&
        !l.canceladoEm &&
        l.devolvidoEm != null && // só um Lote já devolvido pode ser faturado
        l.relavagem === relavagem &&
        dataRefLote(l, modo) >= fatura.periodo.de &&
        dataRefLote(l, modo) <= fatura.periodo.ate,
    )
    .sort(
      (a, b) =>
        dataRefLote(a, modo).localeCompare(dataRefLote(b, modo)) || a.os - b.os,
    );
}

/**
 * O que uma OS entregou, por Tipo: a Devolvida própria e a que voltou de
 * carona do Backlog (Devolvidas de Retidas). Só Tipos com alguma peça. É a
 * mesma conta para as linhas cobradas e para as de relavagem da Planilha.
 */
function entreguePorTipo(snapshot: Snapshot, loteId: Id): Map<Id, { devolvida: number; deRetidas: number }> {
  const tipos = new Set<Id>([
    ...snapshot.contagens.filter((c) => c.loteId === loteId && c.devolvida > 0).map((c) => c.tipoId),
    ...tiposBaixadosDoLote(snapshot, loteId),
  ]);
  const out = new Map<Id, { devolvida: number; deRetidas: number }>();
  for (const tipoId of tipos) {
    const devolvida = snapshot.contagens.find((c) => c.loteId === loteId && c.tipoId === tipoId)?.devolvida ?? 0;
    const deRetidas = baixasDoLote(snapshot, loteId, tipoId);
    if (devolvida + deRetidas > 0) out.set(tipoId, { devolvida, deRetidas });
  }
  return out;
}

function movimentosDe(snapshot: Snapshot, fatura: Fatura): MovimentoFatura[] {
  const modo = snapshot.config.dataFaturamento;
  // emitida: só os Lotes congelados; rascunho: os que caem no período agora
  const lotes = fatura.emitidaEm
    ? snapshot.lotes
        .filter((l) => fatura.loteIds.includes(l.id))
        .sort(
          (a, b) =>
            dataRefLote(a, modo).localeCompare(dataRefLote(b, modo)) ||
            a.os - b.os,
        )
    : devolucoesDoPeriodo(snapshot, fatura);

  const mov: MovimentoFatura[] = [];
  for (const lote of lotes) {
    const data = dataRefLote(lote, modo);
    for (const [tipoId, { devolvida, deRetidas }] of entreguePorTipo(snapshot, lote.id)) {
      const r = precoVigente(snapshot, fatura.clienteId, tipoId, data);
      mov.push({
        loteId: lote.id,
        loteCodigo: lote.codigo,
        os: lote.os,
        data,
        tipoId,
        devolvida,
        deRetidas,
        precoCentavos: r.pendente ? null : r.valorCentavos,
      });
    }
  }
  return mov;
}

export function calcularFatura(snapshot: Snapshot, faturaId: Id): FaturaCalculada {
  const fatura = pegar(snapshot, faturaId);
  const emitida = fatura.emitidaEm != null;
  const movimentos = movimentosDe(snapshot, fatura);

  const ajustesPorTipo = new Map<Id, AjusteFatura[]>();
  const ajustesLivres: AjusteFatura[] = [];
  for (const a of fatura.ajustes) {
    if (a.tipoId == null) ajustesLivres.push(a);
    else {
      const arr = ajustesPorTipo.get(a.tipoId) ?? [];
      arr.push(a);
      ajustesPorTipo.set(a.tipoId, arr);
    }
  }

  let linhas: LinhaFatura[];
  let tiposPendentes: Id[];

  if (emitida) {
    tiposPendentes = [];
    linhas = fatura.linhasCongeladas.map((lc): LinhaFatura => ({
      tipoId: lc.tipoId,
      quantidade: lc.quantidade,
      deRetidas: lc.deRetidas,
      subtotalCentavos: lc.subtotalCentavos,
      unitarioCentavos:
        lc.quantidade > 0 ? Math.round(lc.subtotalCentavos / lc.quantidade) : 0,
      precosCentavos: lc.precosCentavos ?? [],
      ajustes: ajustesPorTipo.get(lc.tipoId) ?? [],
      pendente: false,
    }));
  } else {
    const agg = new Map<
      Id,
      {
        quantidade: number;
        deRetidas: number;
        subtotal: number;
        pendente: boolean;
        /** preço → a data do primeiro uso (para listar em ordem de data) */
        precos: Map<Centavos, DataISO>;
      }
    >();
    const somar = (
      tipoId: Id,
      qtd: number,
      deRetidas: number,
      preco: Centavos | null,
      data: DataISO,
    ): void => {
      const cur = agg.get(tipoId) ?? {
        quantidade: 0,
        deRetidas: 0,
        subtotal: 0,
        pendente: false,
        precos: new Map<Centavos, DataISO>(),
      };
      cur.quantidade += qtd;
      cur.deRetidas += deRetidas;
      if (preco == null) cur.pendente = true;
      else {
        cur.subtotal += qtd * preco;
        const primeira = cur.precos.get(preco);
        if (primeira == null || data < primeira) cur.precos.set(preco, data);
      }
      agg.set(tipoId, cur);
    };
    for (const m of movimentos) {
      somar(m.tipoId, m.devolvida + m.deRetidas, m.deRetidas, m.precoCentavos, m.data);
    }
    // compensações (ADR-0031): pelo preço da data da OS compensada
    for (const c of fatura.compensacoes) {
      for (const p of c.pecas) {
        const r = precoVigente(snapshot, fatura.clienteId, p.tipoId, c.data);
        somar(p.tipoId, p.quantidade, 0, r.pendente ? null : r.valorCentavos, c.data);
      }
    }
    tiposPendentes = [...agg].filter(([, v]) => v.pendente).map(([k]) => k);
    linhas = [...agg].map(([tipoId, v]): LinhaFatura => ({
      tipoId,
      quantidade: v.quantidade,
      deRetidas: v.deRetidas,
      subtotalCentavos: v.subtotal,
      unitarioCentavos:
        v.quantidade > 0 && !v.pendente
          ? Math.round(v.subtotal / v.quantidade)
          : 0,
      precosCentavos: [...v.precos]
        .sort(([pa, da], [pb, db]) => da.localeCompare(db) || pa - pb)
        .map(([preco]) => preco),
      ajustes: ajustesPorTipo.get(tipoId) ?? [],
      pendente: v.pendente,
    }));
  }

  linhas.sort((a, b) => a.tipoId.localeCompare(b.tipoId));

  const subtotalPecasCentavos = linhas.reduce(
    (s, l) => s + l.subtotalCentavos,
    0,
  );
  const ajustesCentavos = fatura.ajustes.reduce((s, a) => s + a.valorCentavos, 0);

  return {
    fatura,
    emitida,
    linhas,
    ajustesLivres,
    movimentos,
    subtotalPecasCentavos,
    ajustesCentavos,
    totalCentavos: subtotalPecasCentavos + ajustesCentavos,
    tiposPendentes,
  };
}

// ------------------------------------------------------ emitir / reabrir ----

function proximoNumero(snapshot: Snapshot, ano: string): string {
  let maior = 0;
  for (const f of snapshot.faturas) {
    if (!f.numero) continue;
    const [y, seq] = f.numero.split("/");
    if (y === ano && Number(seq) > maior) maior = Number(seq);
  }
  return `${ano}/${String(maior + 1).padStart(4, "0")}`;
}

export function emitirFatura(
  snapshot: Snapshot,
  faturaId: Id,
  hoje: DataISO = new Date().toISOString().slice(0, 10),
  /**
   * Vencimento escolhido na emissão. `undefined` = usar a sugestão do Dia de
   * Vencimento do Cliente (#40); `null` = emitir sem vencimento.
   */
  vencimento?: DataISO | null,
): Snapshot {
  const f = pegar(snapshot, faturaId);
  if (f.emitidaEm != null) throw new ErroFatura("Fatura já emitida.");
  if (contaDaFatura(snapshot, faturaId)) {
    throw new ErroFatura("Esta fatura já possui uma conta a receber.");
  }
  if (!isDataISO(hoje)) throw new ErroFatura("Data de emissão inválida.");

  const calc = calcularFatura(snapshot, faturaId);
  if (calc.linhas.length === 0 && f.ajustes.length === 0) {
    throw new ErroFatura("Não há nada para faturar neste período.");
  }
  exigirLinhasNaoNegativas(snapshot, faturaId);
  exigirCompensacoesDeOsFaturada(snapshot, f);
  if (calc.tiposPendentes.length > 0) {
    const nomes = calc.tiposPendentes
      .map((id) => nomeDoTipo(snapshot, id))
      .join(", ");
    throw new ErroFatura(
      `Precifique antes de emitir: ${nomes} sem preço em alguma data do período.`,
    );
  }

  const loteIds = [...new Set(calc.movimentos.map((m) => m.loteId))];
  const linhasCongeladas: LinhaFaturaCongelada[] = calc.linhas.map((l) => ({
    tipoId: l.tipoId,
    quantidade: l.quantidade,
    deRetidas: l.deRetidas,
    subtotalCentavos: l.subtotalCentavos,
    precosCentavos: l.precosCentavos,
  }));

  if (vencimento != null && !isDataISO(vencimento)) {
    throw new ErroFatura("Vencimento inválido.");
  }
  const cliente = snapshot.clientes.find((c) => c.id === f.clienteId);
  const venc =
    vencimento !== undefined
      ? vencimento
      : cliente
        ? vencimentoSugerido(cliente, f.periodo.ate)
        : null;

  const emitida = trocar(snapshot, {
    ...f,
    numero: proximoNumero(snapshot, hoje.slice(0, 4)),
    emitidaEm: hoje,
    loteIds,
    linhasCongeladas,
    vencimento: venc,
  });

  // Emitir cria a Conta a Receber (ADR-0011): a receita de Cliente tem uma casa
  // só, e toda visão financeira lê Contas sem precisar conhecer Fatura.
  // A Categoria de sistema é invariante do domínio, não escolha do usuário:
  // garante-se em vez de falhar na cara de quem está faturando.
  const comCat = garantirCategoriaFaturamento(emitida);
  const cat = categoriaFaturamento(comCat)!;
  const numero = emitida.faturas.find((x) => x.id === faturaId)?.numero ?? "";
  return criarConta(comCat, {
    direcao: "receber",
    descricao: `Fatura ${numero}`.trim(),
    valorCentavos: calc.totalCentavos,
    // sem vencimento cadastrado, a Conta vence na data de emissão — melhor uma
    // data honesta que um campo vazio numa cobrança
    vencimento: venc ?? hoje,
    categoriaId: cat.id,
    fonte: "cliente",
    clienteId: f.clienteId,
    faturaId,
  });
}

export function reabrirFatura(
  snapshot: Snapshot,
  faturaId: Id,
  justificativa: string,
  hoje: DataISO = new Date().toISOString().slice(0, 10),
): Snapshot {
  const f = pegar(snapshot, faturaId);
  if (f.emitidaEm == null) throw new ErroFatura("A Fatura não está emitida.");
  const texto = justificativa.trim();
  if (texto === "") throw new ErroFatura("Informe a justificativa da reabertura.");

  // Reabrir apaga a Conta a Receber que a emissão criou (ADR-0011). Se ela já
  // recebeu algum Pagamento, reabrir é bloqueado: apagar a Conta apagaria o
  // registro de um dinheiro que de fato entrou.
  const conta = contaDaFatura(snapshot, faturaId);
  if (conta && totalPago(snapshot, conta.id) > 0) {
    throw new ErroFatura(
      "Esta fatura já teve recebimento registrado. Remova os recebimentos em " +
        "Contas a Receber antes de reabrir.",
    );
  }
  // ADR-0031: OS desta fatura já compensada numa fatura EMITIDA — reabrir
  // deixaria corrigi-la de novo pelo Fechamento (correção em dobro). Com a
  // compensação ainda em rascunho, é a emissão dela que recusa.
  for (const outra of snapshot.faturas) {
    if (outra.id === faturaId || outra.emitidaEm == null) continue;
    const c = outra.compensacoes.find((x) => x.loteId != null && f.loteIds.includes(x.loteId));
    if (c) {
      throw new ErroFatura(
        `A OS ${c.os} foi compensada na fatura ${outra.numero ?? ""} — reabra aquela e remova a compensação antes.`,
      );
    }
  }
  const semConta = removerContaDaFatura(snapshot, faturaId);

  // ADR-0024: a OS (Lote) precisa registrar que passou por aqui — capturado
  // ANTES de `loteIds` zerar embaixo, senão a OS fica indistinguível de uma
  // que nunca foi faturada (situacaoLote cairia direto pra "fechado").
  const afetados = new Set(f.loteIds);
  const comLotesMarcados: Snapshot = {
    ...semConta,
    lotes: semConta.lotes.map((l) =>
      afetados.has(l.id) ? { ...l, reabertoEm: hoje } : l,
    ),
  };

  return trocar(comLotesMarcados, {
    ...f,
    numero: null,
    emitidaEm: null,
    loteIds: [],
    linhasCongeladas: [],
    vencimento: null,
    reaberturas: [...f.reaberturas, { em: hoje, justificativa: texto }],
  });
}

// -------------------------------------------------- planilha de fechamento ----

export interface LinhaPlanilha {
  data: DataISO;
  /** OS desta linha: a da Devolução (1 linha = 1 Devolução = 1 Lote), ou a
   * OS compensada, na linha de Compensação de Peças. */
  os: number;
  /** Código interno do Lote (`AAA-2026-0001`); `null` na compensação. */
  loteCodigo: string | null;
  /** quantidade por Tipo (devolvida + de retidas somadas). */
  porTipo: Map<Id, number>;
  total: number;
  /** Peças que voltaram do Backlog nesta Devolução (para o marcador). */
  deRetidas: number;
  /**
   * De onde vem a linha:
   * - `lote`: uma OS do período;
   * - `relavagem`: OS de relavagem — mostrada, mas fora de todos os totais
   *   (ADR-0015);
   * - `compensacao`: Compensação de Peças (ADR-0031), "(Comp.) nº" com a
   *   data daquela OS — conta nos totais.
   */
  origem: OrigemLinhaPlanilha;
}

export type OrigemLinhaPlanilha = "lote" | "relavagem" | "compensacao";

/** Ordem das linhas da Planilha (e da lista de compensações): data, depois OS. */
export function porDataEOs(a: { data: DataISO; os: number }, b: { data: DataISO; os: number }): number {
  return a.data.localeCompare(b.data) || a.os - b.os;
}

export interface PlanilhaFechamento {
  fatura: Fatura;
  emitida: boolean;
  /** Ids dos Tipos movimentados, na ordem em que aparecem nas colunas. */
  tipos: Id[];
  linhas: LinhaPlanilha[];
  totalPorTipo: Map<Id, number>;
  totalPecas: number;
  subtotalPorTipo: Map<Id, Centavos>;
  unitarioPorTipo: Map<Id, Centavos>;
  /** Os preços usados de cada Tipo, em ordem de data (ver `LinhaFatura`). */
  precosPorTipo: Map<Id, Centavos[]>;
  ajustes: AjusteFatura[];
  subtotalPecasCentavos: Centavos;
  totalCentavos: Centavos;
}

/**
 * Matriz da Planilha de Fechamento: uma linha por Devolução (data + OS do Lote),
 * Tipos movimentados nas colunas. As Devolvidas de Retidas somam na mesma célula.
 * `ordemTipos` (nomes) só serve para ordenar as colunas.
 */
export function planilhaFechamento(
  snapshot: Snapshot,
  faturaId: Id,
  ordemTipos: (a: Id, b: Id) => number = (a, b) => a.localeCompare(b),
): PlanilhaFechamento {
  const calc = calcularFatura(snapshot, faturaId);

  const porLote = new Map<Id, LinhaPlanilha>();
  const totalPorTipo = new Map<Id, number>();
  const tiposVistos = new Set<Id>();

  for (const m of calc.movimentos) {
    const qtd = m.devolvida + m.deRetidas;
    if (qtd === 0) continue;
    tiposVistos.add(m.tipoId);
    totalPorTipo.set(m.tipoId, (totalPorTipo.get(m.tipoId) ?? 0) + qtd);

    const linha =
      porLote.get(m.loteId) ??
      {
        data: m.data,
        os: m.os,
        loteCodigo: m.loteCodigo,
        porTipo: new Map(),
        total: 0,
        deRetidas: 0,
        origem: "lote",
      };
    linha.porTipo.set(m.tipoId, (linha.porTipo.get(m.tipoId) ?? 0) + qtd);
    linha.total += qtd;
    linha.deRetidas += m.deRetidas;
    porLote.set(m.loteId, linha);
  }

  // ADR-0015: OSs de relavagem entram na planilha para dar visibilidade ao hotel,
  // mas suas peças NÃO entram em nenhum dos totais acumulados. Mesma conta e
  // mesmo período das linhas cobradas (`entreguePorTipo`, `devolucoesDoPeriodo`).
  const modo = snapshot.config.dataFaturamento;
  for (const lote of devolucoesDoPeriodo(snapshot, calc.fatura, true)) {
    const linha: LinhaPlanilha = {
      data: dataRefLote(lote, modo),
      os: lote.os,
      loteCodigo: lote.codigo,
      porTipo: new Map(),
      total: 0,
      deRetidas: 0,
      origem: "relavagem",
    };
    for (const [tipoId, { devolvida, deRetidas }] of entreguePorTipo(snapshot, lote.id)) {
      const qtd = devolvida + deRetidas;
      tiposVistos.add(tipoId);
      linha.porTipo.set(tipoId, qtd);
      linha.total += qtd;
      linha.deRetidas += deRetidas;
    }
    porLote.set(lote.id, linha);
  }

  // ADR-0031: cada compensação é uma linha própria, contando nos totais
  const compensacoes: LinhaPlanilha[] = calc.fatura.compensacoes.map((c) => {
    const porTipo = new Map<Id, number>();
    for (const p of c.pecas) {
      porTipo.set(p.tipoId, p.quantidade);
      tiposVistos.add(p.tipoId);
      totalPorTipo.set(p.tipoId, (totalPorTipo.get(p.tipoId) ?? 0) + p.quantidade);
    }
    return {
      data: c.data,
      os: c.os,
      loteCodigo: null,
      porTipo,
      total: c.pecas.reduce((a, p) => a + p.quantidade, 0),
      deRetidas: 0,
      origem: "compensacao",
    };
  });

  const tipos = [...tiposVistos].sort(ordemTipos);
  const linhas = [...porLote.values(), ...compensacoes].sort(porDataEOs);

  const subtotalPorTipo = new Map<Id, Centavos>();
  const unitarioPorTipo = new Map<Id, Centavos>();
  const precosPorTipo = new Map<Id, Centavos[]>();
  for (const l of calc.linhas) {
    subtotalPorTipo.set(l.tipoId, l.subtotalCentavos);
    unitarioPorTipo.set(l.tipoId, l.unitarioCentavos);
    precosPorTipo.set(l.tipoId, l.precosCentavos);
  }

  return {
    fatura: calc.fatura,
    emitida: calc.emitida,
    tipos,
    linhas,
    totalPorTipo,
    totalPecas: [...totalPorTipo.values()].reduce((a, n) => a + n, 0),
    subtotalPorTipo,
    unitarioPorTipo,
    precosPorTipo,
    ajustes: calc.fatura.ajustes,
    subtotalPecasCentavos: calc.subtotalPecasCentavos,
    totalCentavos: calc.totalCentavos,
  };
}

// ------------------------------------------------------------- leitura ----

export type SituacaoFatura = "rascunho" | "emitida" | "reaberta" | "paga";

/**
 * A Situação da Fatura. Depois do ADR-0011 a Fatura NÃO tem status de pagamento
 * próprio: ela espelha a Situação de Pagamento da Conta a Receber que gerou.
 * Quem dá baixa é a tela de Contas a Receber.
 */
export function situacaoFatura(snapshot: Snapshot, f: Fatura): SituacaoFatura {
  if (f.emitidaEm == null) {
    if (f.reaberturas.length > 0) return "reaberta";
    return "rascunho";
  }
  const conta = contaDaFatura(snapshot, f.id);
  if (conta && situacaoPagamento(snapshot, conta) === "quitada") return "paga";
  if (f.reaberturas.length > 0) return "reaberta";
  return "emitida";
}

/** As Faturas emitidas que já cobrem uma Devolução deste Lote (via `loteEstaFaturado`). */
export function faturaDoLote(
  snapshot: Pick<Snapshot, "faturas">,
  loteId: Id,
): Fatura | null {
  return (
    snapshot.faturas.find(
      (f) => f.emitidaEm != null && f.loteIds.includes(loteId),
    ) ?? null
  );
}

export { loteEstaFaturado };

// ------------------------------------------------- migração do ADR-0011 ----

/**
 * Cria a Conta a Receber das Faturas emitidas que ainda não têm uma (issue
 * #41). É a migração de dados do ADR-0011, e roda no boot.
 *
 * Não é SQL de propósito: o valor de uma Fatura é **calculado**
 * (`calcularFatura`, sobre linhas congeladas e ajustes), e refazer essa soma
 * em SQL criaria uma segunda definição do total, livre para divergir. Aqui a
 * fonte da verdade é a mesma que a emissão usa.
 *
 * `pagasLegado` são os ids que tinham `fatura.pago = 1` antes da migração v21;
 * cada uma ganha um Pagamento único na data de emissão, o que a deixa quitada.
 *
 * Idempotente: Fatura que já tem Conta é pulada.
 */
export function garantirContasDeFaturas(
  snapshot: Snapshot,
  pagasLegado: readonly Id[] = [],
): Snapshot {
  const pagas = new Set(pagasLegado);
  let out = garantirCategoriaFaturamento(snapshot);
  const cat = categoriaFaturamento(out)!;

  for (const f of snapshot.faturas) {
    if (f.emitidaEm == null) continue;
    if (contaDaFatura(out, f.id)) continue;

    const total = calcularFatura(out, f.id).totalCentavos;
    out = criarConta(out, {
      direcao: "receber",
      descricao: `Fatura ${f.numero ?? ""}`.trim(),
      valorCentavos: total,
      vencimento: f.vencimento ?? f.emitidaEm,
      categoriaId: cat.id,
      fonte: "cliente",
      clienteId: f.clienteId,
      faturaId: f.id,
    });

    // Fatura que estava marcada como paga nasce quitada, com um Pagamento
    // único na data de emissão — a data real não existe no dado antigo.
    if (pagas.has(f.id) && total > 0) {
      const conta = contaDaFatura(out, f.id);
      if (conta) {
        out = registrarPagamento(out, conta.id, {
          data: f.emitidaEm,
          valorCentavos: total,
        });
      }
    }
  }
  return out;
}

export interface ImpactoPreco {
  ossAbertasAfetadas: number;
  /** Compensações de Peças (ADR-0031) em fatura não emitida cujo preço muda. */
  compensacoesAfetadas: number;
  deltaCentavos: Centavos;
}

export function calcularImpactoPrecoRetroativo(
  snapAnterior: Snapshot,
  snapNovo: Snapshot,
  desde: DataISO,
): ImpactoPreco {
  let ossAfetadas = new Set<Id>();
  let delta = 0;

  const modo = snapAnterior.config.dataFaturamento;
  const lotesAvaliados = snapAnterior.lotes.filter(l => 
    !l.canceladoEm &&
    l.devolvidoEm != null &&
    !l.relavagem &&
    !loteEstaFaturado(snapAnterior, l.id)
  );

  for (const lote of lotesAvaliados) {
    const dataRef = dataRefLote(lote, modo);
    if (dataRef < desde) continue;

    const contagens = snapAnterior.contagens.filter(c => c.loteId === lote.id);
    const tipos = new Set<Id>([...contagens.map(c => c.tipoId), ...tiposBaixadosDoLote(snapAnterior, lote.id)]);

    let loteAfetado = false;
    for (const tipoId of tipos) {
      const ant = precoVigente(snapAnterior, lote.clienteId, tipoId, dataRef);
      const nov = precoVigente(snapNovo, lote.clienteId, tipoId, dataRef);
      
      const centAnt = ant.pendente ? 0 : ant.valorCentavos;
      const centNov = nov.pendente ? 0 : nov.valorCentavos;
      if (centAnt !== centNov) {
        loteAfetado = true;
        const devolvida = contagens.find(c => c.tipoId === tipoId)?.devolvida ?? 0;
        const deRetidas = baixasDoLote(snapAnterior, lote.id, tipoId);
        const qtd = devolvida + deRetidas;
        delta += (centNov - centAnt) * qtd;
      }
    }
    if (loteAfetado) ossAfetadas.add(lote.id);
  }

  // ADR-0031: a compensação é cobrada pelo preço da data da OS compensada —
  // numa fatura ainda não emitida, ela muda junto com a vigência
  let compensacoesAfetadas = 0;
  for (const f of snapAnterior.faturas) {
    if (f.emitidaEm != null) continue;
    for (const c of f.compensacoes) {
      if (c.data < desde) continue;
      let afetada = false;
      for (const p of c.pecas) {
        const ant = precoVigente(snapAnterior, f.clienteId, p.tipoId, c.data);
        const nov = precoVigente(snapNovo, f.clienteId, p.tipoId, c.data);
        const centAnt = ant.pendente ? 0 : ant.valorCentavos;
        const centNov = nov.pendente ? 0 : nov.valorCentavos;
        if (centAnt !== centNov) {
          afetada = true;
          delta += (centNov - centAnt) * p.quantidade;
        }
      }
      if (afetada) compensacoesAfetadas++;
    }
  }

  return { ossAbertasAfetadas: ossAfetadas.size, compensacoesAfetadas, deltaCentavos: delta };
}
